import type { DrawingToolInstance, ChartLayout, ChartPriceRange, DraggingOverride, Point } from "./types";
import { getHorizontalLineHandleX } from "./drawHorizontalLine";
import { getVerticalLineHandleY } from "./drawVerticalLine";
import { priceToY } from "./drawingDragHelpers";

const HIT_RADIUS = 8;
const TWO_POINT_TOOLS = new Set(["trendLine", "rayLine", "rectangle", "fibonacciRetracement"]);

export function findHorizontalHandle(
  mouseX: number, mouseY: number,
  instances: DrawingToolInstance[],
  override: DraggingOverride | null,
  layout: ChartLayout,
  range: ChartPriceRange,
): DrawingToolInstance | null {
  const handleX = getHorizontalLineHandleX(layout);
  for (const inst of instances) {
    if (inst.name !== "horizontalLine" || inst.coordinates?.kind !== "single-price") continue;
    const price = override?.kind === "price" && override.id === inst.id
      ? override.price : inst.coordinates.price;
    if (Math.hypot(mouseX - handleX, mouseY - priceToY(price, layout, range)) <= HIT_RADIUS)
      return inst;
  }
  return null;
}

export function findVerticalHandle(
  mouseX: number, mouseY: number,
  instances: DrawingToolInstance[],
  override: DraggingOverride | null,
  layout: ChartLayout,
  timestampToX: (ts: number) => number,
): DrawingToolInstance | null {
  const handleY = getVerticalLineHandleY(layout);
  for (const inst of instances) {
    if (inst.name !== "verticalLine" || inst.coordinates?.kind !== "single-timestamp") continue;
    const ts = override?.kind === "timestamp" && override.id === inst.id
      ? override.timestamp : inst.coordinates.timestamp;
    if (Math.hypot(mouseX - (timestampToX(ts) + 2), mouseY - handleY) <= HIT_RADIUS)
      return inst;
  }
  return null;
}

export function findTwoPointHandle(
  mouseX: number, mouseY: number,
  instances: DrawingToolInstance[],
  override: DraggingOverride | null,
  layout: ChartLayout,
  range: ChartPriceRange,
  timestampToX: (ts: number) => number,
): { inst: DrawingToolInstance; pointKey: "point1" | "point2" } | null {
  for (const inst of instances) {
    if (!TWO_POINT_TOOLS.has(inst.name) || inst.coordinates?.kind !== "two-point") continue;
    const pts = override?.kind === "twoPoint" && override.id === inst.id ? override : inst.coordinates;
    for (const pointKey of ["point1", "point2"] as const) {
      const pt: Point = pts[pointKey];
      if (Math.hypot(mouseX - timestampToX(pt.timestamp), mouseY - priceToY(pt.price, layout, range)) <= HIT_RADIUS)
        return { inst, pointKey };
    }
  }
  return null;
}

export function findThreePointHandle(
  mouseX: number, mouseY: number,
  instances: DrawingToolInstance[],
  override: DraggingOverride | null,
  layout: ChartLayout,
  range: ChartPriceRange,
  timestampToX: (ts: number) => number,
): { inst: DrawingToolInstance; pointKey: "point1" | "point2" | "point3" } | null {
  for (const inst of instances) {
    if (inst.name !== "parallelChannel" || inst.coordinates?.kind !== "three-point") continue;
    const pts = override?.kind === "threePoint" && override.id === inst.id ? override : inst.coordinates;
    const hx1 = timestampToX(pts.point1.timestamp);
    const hx2 = timestampToX(pts.point2.timestamp);
    const handles: [number, number, "point1" | "point2" | "point3"][] = [
      [hx1, priceToY(pts.point1.price, layout, range), "point1"],
      [hx2, priceToY(pts.point2.price, layout, range), "point2"],
      [(hx1 + hx2) / 2, priceToY(pts.point3.price, layout, range), "point3"],
    ];
    for (const [hx, hy, pointKey] of handles) {
      if (Math.hypot(mouseX - hx, mouseY - hy) <= HIT_RADIUS) return { inst, pointKey };
    }
  }
  return null;
}
